import { useMemo, useState } from 'react';
import { LayoutGrid, Plus, Trash2 } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { EntitySelect } from '@/components/EntitySelect';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { useSession } from '@/stores/session';
import { MODULE_STATUSES } from '../constants';
import { modulesHooks, projectMembersHooks } from '../queries';
import { makeProjectMemberResolve, makeProjectMemberSearch, projectMemberName } from '../select';
import type { Module, ModuleStatus, Project, ProjectMember } from '../types';
import { FilterSelect, FiltersPopover, ListSearch } from '@/components/ui/list-filters';
import { reorderRows } from '../reorder';
import { ReorderButtons } from './ReorderButtons';

const moduleStatusLabel = (s: string) => MODULE_STATUSES.find((x) => x.value === s)?.label ?? s;

function rows<T>(raw: { results: T[] } | T[] | undefined): T[] {
  return (Array.isArray(raw) ? raw : (raw?.results ?? [])) as T[];
}

export function ProjectModulesTab({ project }: { project: Project }) {
  const query = modulesHooks.useList({ project: project.id });
  const update = modulesHooks.useUpdate();
  const remove = modulesHooks.useRemove();
  // Guests read modules but can't manage them (create/edit/delete stay staff).
  const isGuest = useSession((s) => s.role) === 'guest';
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<Module | null>(null);
  const [search, setSearch] = useState('');
  const [statusFil, setStatusFil] = useState('all');
  const [leadFil, setLeadFil] = useState('all');

  // Lead is a project member — scope the filter options to this project's
  // members, not the whole org roster.
  const membersData = projectMembersHooks.useList({ project: project.id }).data;
  const memberOptions = useMemo(
    () =>
      rows<ProjectMember>(membersData).map((pm) => ({
        value: String(pm.member),
        label: projectMemberName(pm),
      })),
    [membersData],
  );

  const allModules = useMemo(
    () => rows<Module>(query.data).sort((a, b) => a.order - b.order),
    [query.data],
  );
  const modules = useMemo(() => {
    const q = search.trim().toLowerCase();
    return allModules.filter(
      (m) =>
        (!q || m.name.toLowerCase().includes(q)) &&
        (statusFil === 'all' || m.status === statusFil) &&
        (leadFil === 'all' || String(m.lead ?? '') === leadFil),
    );
  }, [allModules, search, statusFil, leadFil]);

  const activeFilters = [statusFil, leadFil].filter((v) => v !== 'all').length;

  // Reordering swaps neighbours, so it needs the full list on screen — hide the
  // controls while a search/filter shows only a subset.
  const canReorder = !isGuest && search.trim() === '' && activeFilters === 0;
  const move = (index: number, dir: -1 | 1) =>
    reorderRows(modules, index, dir, (m, order) =>
      update.mutate({ id: m.public_id, input: { order } }),
    );

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        {allModules.length > 0 ? (
          <div className="flex flex-wrap items-center gap-2">
            <ListSearch value={search} onChange={setSearch} placeholder="Search modules…" />
            <FiltersPopover
              activeFilters={activeFilters}
              onClear={() => {
                setStatusFil('all');
                setLeadFil('all');
              }}
            >
              <FilterSelect
                label="Status" value={statusFil} onChange={setStatusFil} allLabel="All statuses"
                options={MODULE_STATUSES.map((s) => ({ value: s.value, label: s.label }))}
              />
              <FilterSelect
                label="Lead" value={leadFil} onChange={setLeadFil} allLabel="Anyone"
                options={memberOptions}
              />
            </FiltersPopover>
          </div>
        ) : (
          <span />
        )}
        {!isGuest ? (
          <Button size="sm" onClick={() => setCreateOpen(true)}>
            <Plus className="size-4" /> Add Module
          </Button>
        ) : null}
      </div>

      {query.isPending ? (
        <Skeleton className="h-40 w-full" />
      ) : allModules.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <LayoutGrid className="text-muted-foreground/30 size-16" strokeWidth={1.25} />
          <h2 className="mt-6 text-lg font-semibold">Map your project goals to Modules and track easily.</h2>
          <p className="text-muted-foreground mt-1 max-w-md text-sm">
            Modules are made up of interconnected work items. They help monitor progress through project
            phases, each with its own deadline and owner.
          </p>
          {!isGuest ? (
            <Button className="mt-5" onClick={() => setCreateOpen(true)}>
              <Plus className="size-4" /> Set your first module
            </Button>
          ) : null}
        </div>
      ) : modules.length === 0 ? (
        <p className="text-muted-foreground py-10 text-center text-sm">No modules match your filters.</p>
      ) : (
        <ul className="space-y-2">
          {modules.map((m, i) => (
            <li key={m.id} className="flex items-center gap-3 rounded-md border px-3 py-2.5">
              {canReorder ? (
                <ReorderButtons index={i} count={modules.length} onMove={(dir) => move(i, dir)} />
              ) : null}
              <LayoutGrid className="text-muted-foreground size-4 shrink-0" />
              {isGuest ? (
                <span className="flex-1 truncate text-sm font-medium">{m.name}</span>
              ) : (
                <button
                  type="button"
                  onClick={() => setEditing(m)}
                  className="hover:text-primary flex-1 truncate text-left text-sm font-medium"
                >
                  {m.name}
                </button>
              )}
              {m.start_date || m.end_date ? (
                <span className="text-muted-foreground text-xs">
                  {m.start_date ?? '…'} → {m.end_date ?? '…'}
                </span>
              ) : null}
              <Select
                value={m.status}
                onValueChange={(v) => update.mutate({ id: m.public_id, input: { status: v as ModuleStatus } })}
                disabled={isGuest}
              >
                <SelectTrigger className="h-8 w-36">
                  <SelectValue>
                    <Badge variant="outline">{moduleStatusLabel(m.status)}</Badge>
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {MODULE_STATUSES.map((s) => (
                    <SelectItem key={s.value} value={s.value}>
                      {s.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {!isGuest ? (
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-muted-foreground hover:text-destructive"
                  onClick={() => remove.mutate(m.public_id)}
                  aria-label={`Delete ${m.name}`}
                >
                  <Trash2 className="size-4" />
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
      )}

      <ModuleFormDialog project={project} open={createOpen} onOpenChange={setCreateOpen} />
      <ModuleFormDialog
        project={project}
        module={editing}
        open={editing !== null}
        onOpenChange={(o) => !o && setEditing(null)}
      />
    </div>
  );
}

function ModuleFormDialog({
  project,
  module,
  open,
  onOpenChange,
}: {
  project: Project;
  module?: Module | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const create = modulesHooks.useCreate();
  const update = modulesHooks.useUpdate();
  const isEdit = !!module;
  // Remount the form per module/create so fields reset to the right defaults.
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span className="bg-muted/60 flex items-center gap-1.5 rounded-md px-2 py-0.5 text-sm font-normal">
              <span>{project.icon || '📁'}</span> {project.name}
            </span>
            {isEdit ? 'Edit module' : 'Create module'}
          </DialogTitle>
        </DialogHeader>
        <ModuleForm
          key={module?.id ?? 'new'}
          project={project}
          module={module}
          pending={create.isPending || update.isPending}
          onCancel={() => onOpenChange(false)}
          onSubmit={(payload) => {
            if (isEdit && module) {
              update.mutate(
                { id: module.public_id, input: payload },
                { onSuccess: () => onOpenChange(false) },
              );
            } else {
              create.mutate(
                { project: project.id, ...payload },
                { onSuccess: () => onOpenChange(false) },
              );
            }
          }}
        />
      </DialogContent>
    </Dialog>
  );
}

interface ModulePayload {
  name: string;
  description?: string;
  status: ModuleStatus;
  lead: number | null;
  start_date: string | null;
  end_date: string | null;
}

function ModuleForm({
  project,
  module,
  pending,
  onCancel,
  onSubmit,
}: {
  project: Project;
  module?: Module | null;
  pending: boolean;
  onCancel: () => void;
  onSubmit: (payload: ModulePayload) => void;
}) {
  const [name, setName] = useState(module?.name ?? '');
  const [description, setDescription] = useState(module?.description ?? '');
  const [status, setStatus] = useState<ModuleStatus>(module?.status ?? 'backlog');
  const [lead, setLead] = useState(module?.lead ? String(module.lead) : '');
  const [start, setStart] = useState(module?.start_date ?? '');
  const [end, setEnd] = useState(module?.end_date ?? '');
  // Lead can only be a member of this project — scope the picker accordingly.
  const leadSearch = useMemo(() => makeProjectMemberSearch(project.id), [project.id]);
  const leadResolve = useMemo(() => makeProjectMemberResolve(project.id), [project.id]);

  function submit() {
    if (!name.trim()) return;
    onSubmit({
      name: name.trim(),
      description: description.trim() || undefined,
      status,
      lead: lead ? Number(lead) : null,
      start_date: start || null,
      end_date: end || null,
    });
  }

  return (
    <>
      <Input
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Title"
        className="h-auto border-0 px-0 text-base shadow-none focus-visible:ring-0"
      />
      <Textarea
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        placeholder="Description"
        rows={3}
      />

      <div className="flex flex-wrap items-end gap-2">
        <div className="space-y-1">
          <Label className="text-xs">Start date</Label>
          <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="h-8 w-40" />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">End date</Label>
          <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="h-8 w-40" />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Status</Label>
          <Select value={status} onValueChange={(v) => setStatus(v as ModuleStatus)}>
            <SelectTrigger className="h-8 w-36"><SelectValue /></SelectTrigger>
            <SelectContent>
              {MODULE_STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Lead</Label>
          <div className="w-44">
            <EntitySelect
              queryKey={`module-lead-${project.id}`}
              value={lead}
              onChange={setLead}
              search={leadSearch}
              resolveLabel={leadResolve}
              placeholder="None"
              clearable
            />
          </div>
        </div>
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button onClick={submit} disabled={!name.trim() || pending}>
          {pending ? 'Saving…' : module ? 'Save changes' : 'Create Module'}
        </Button>
      </DialogFooter>
    </>
  );
}
